import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ethers } from 'ethers';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useInput } from 'nfty-common/lib/hooks/form.hooks';
import React, { useContext } from 'react';
import { BidModalModel } from '../model/bid-modal.model';
import { AppContext } from '../stores/app.store';
import { WalletError } from '../stores/wallet.store';
import { CryptoValue } from './crypto-value.component';

export interface PlaceBidModalProps {
    model: BidModalModel;
    currentBid?: number;
    closeToggle: React.MouseEventHandler<HTMLElement>;
}

export const PlaceBidModal = observer((props: PlaceBidModalProps) => {
    const { closeToggle, currentBid, model } = props;

    const { auctionStore, walletStore } = useContext(AppContext);
    const bidInput = useInput('');

    autorun(() => {
        if (bidInput.value === '' && model.priceWhenOpened !== '') {
            const realValue = ethers.utils.parseEther(model.priceWhenOpened);
            const plusTenPercent = realValue.add(realValue.div(10));
            bidInput.setValue(ethers.utils.formatEther(plusTenPercent));
            walletStore.recommendGas().then((result) => {
                model.setGasEstimate(result);
            });
        }
    });

    async function onSubmitBid() {
        model.reset(true);
        await walletStore.refreshWalletInfo();
        try {
            const tx = await walletStore.submitBid(bidInput.value, model.gas);
            if (tx) {
                model.setSuccessMessage(bidInput.value);
            } else {
                throw new Error('Bid rejected');
            }
        } catch (error) {
            if (error instanceof WalletError) {
                model.setErrorMessage(error.message);
            } else {
                model.setErrorMessage('Unable to submit bid');
            }
        } finally {
            await walletStore.refreshWalletInfo();
            model.setIsLoading(false);
        }
    }

    function onConnect() {
        walletStore.userConnect();
    }

    if (!walletStore.isConnectedToChain) {
        return (
            <div className="modal-content bg-dark border border-secondary">
                <div className="w-100 position-absolute top-0 d-flex justify-content-end p-1">
                    <button style={{ filter: 'invert(1)' }} type="button" className="btn-close btn-light" onClick={closeToggle}></button>
                </div>
                <div className="p-3 d-flex flex-column justify-content-center align-items-center">
                    <div className="m-5 text-center">
                        <FontAwesomeIcon className="text-secondary" icon="hand-holding-usd" size="4x" />
                        <h5 className="mt-4 text-avenir-re text-secondary">
                            <span className="me-2">You will need a wallet to bid</span>
                        </h5>
                    </div>
                    {walletStore.hasValidProvider
                        ? <button className="w-100 btn btn-light" onClick={onConnect}>Connect</button>
                        : <a className="w-100 btn btn-light" target="_blank" href="https://metamask.io/download" rel="noreferrer">Get Metamask</a>
                    }
                </div>
            </div>
        );
    }

    return (
        <div className="modal-content bg-dark border border-secondary">
            {(!model.isLoading && model.errorMessage) && <div className="bg-danger rounded-top text-center p-2">{model.errorMessage}</div>}
            {(!model.isLoading && model.successMessage) &&
                <div className="bg-primary rounded-top text-center p-2">
                    <div>Successfully submitted bid!</div>
                </div>
            }
            {(model.isLoading) &&
                <div className="bg-secondary rounded-top text-center p-2">
                    <div>Waiting for network confirmation. Expected wait time {model.gas.waitTime} minutes</div>
                </div>
            }
            <div className="modal-header border-secondary">
                <h5 className="modal-title text-avenir-re text-secondary">
                    <span className="me-2">Prepare Bid:</span>
                    <em>{auctionStore.auctionData?.title}</em>
                </h5>
                <button style={{ filter: 'invert(1)' }} type="button" className="btn-close btn-light" onClick={closeToggle}></button>
            </div>
            <div className="modal-body">
                <div>
                    <div className="row pb-2">
                        <div className="text-muted col-4">
                            <small><strong>Wallet Address</strong></small>
                        </div>
                        <div className="col-8">
                            <span title={walletStore.walletAddress}>{walletStore.walletAddress?.substr(0, 6)}...{walletStore.walletAddress?.slice(-4)}</span>
                        </div>
                    </div>
                    <div className="row pb-2">
                        <div className="text-muted col-4">
                            <small><strong>Available Funds</strong></small>
                        </div>
                        <div className="col-8">
                            <CryptoValue value={walletStore.walletFunds} />
                        </div>
                    </div>
                    <div className="row pb-2">
                        <div className="text-muted col-4">
                            <small><strong>Current Bid</strong></small>
                        </div>
                        <div className="col-8">
                            <CryptoValue value={currentBid} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-footer border-secondary justify-content-end">
                <div className="input-group">
                    <input type="text" className="form-control border-secondary" {...bidInput.bind} />
                    <button disabled={model.isLoading} className="btn btn-secondary" type="button" onClick={onSubmitBid}>
                        {(!model.isLoading) && <span>Submit Bid</span>}
                        {(model.isLoading) && <FontAwesomeIcon size="sm" pulse={true} icon={['fas', 'spinner']} />}
                    </button>
                </div>
            </div>
        </div>
    );
});